import { useEffect, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SOURCING_TYPES,
  STOCK_UNITS,
  formatQuantity,
  paiseToRupees,
  parseQuantity,
  parseRupees,
  type SourcingRequirement,
  type SourcingType,
  type StockUnit,
} from '@tailoring-crm/shared';
import {
  useCreateSourcingRequirement,
  useUpdateSourcingRequirement,
} from '../../lib/sourcing/use-sourcing.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  useToast,
} from '@tailoring-crm/ui';

/**
 * Writing down what an order needs, or correcting it.
 *
 * THE GARMENT FIELD OFFERS ONLY THE GARMENTS OF THE ORDER IN CONTEXT, and nothing else — the API
 * refuses an item belonging to another order with a 400, and a picker that could offer one would
 * be inviting the shop to make that mistake. "The whole order" is the first option and the default,
 * because a bolt of lining bought for a booking of four blouses genuinely belongs to none of them.
 *
 * NEITHER NUMBER IS TYPED AS A FLOAT. The quantity goes through `parseQuantity`, which reads
 * "3.75" by splitting on the decimal point rather than multiplying — `3.75 * 1000` is not 3750 in
 * binary floating point — and the amount goes through `parseRupees`, which does the same for
 * money. Both come back as the integers the contract requires.
 *
 * The order and the garment are FIXED once the row exists: the API omits them from the update
 * shape, so on an edit the two fields are shown as read-only context rather than offered again.
 */
export interface SourcingFormItem {
  id: string;
  garmentTypeLabel: string;
  quantity: number;
}

export interface SourcingFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId: string;
  orderNumber: string;
  /** The garments of THIS order. Nothing else may be linked — see the note above. */
  items: readonly SourcingFormItem[];
  /** The row being corrected, or null to write a new one down. */
  requirement: SourcingRequirement | null;
  onSaved: () => void;
}

interface Draft {
  orderItemId: string;
  type: SourcingType;
  description: string;
  quantity: string;
  unit: StockUnit;
  amount: string;
  notes: string;
}

const EMPTY: Draft = {
  orderItemId: '',
  type: 'fabrics',
  description: '',
  quantity: '',
  unit: 'metre',
  amount: '',
  notes: '',
};

function draftOf(requirement: SourcingRequirement | null): Draft {
  if (!requirement) return EMPTY;
  return {
    orderItemId: requirement.orderItemId ?? '',
    type: requirement.type,
    description: requirement.description ?? '',
    quantity: formatQuantity(requirement.quantity, requirement.unit),
    unit: requirement.unit,
    amount: requirement.amountPaise > 0 ? String(paiseToRupees(requirement.amountPaise)) : '',
    notes: requirement.notes ?? '',
  };
}

export function SourcingFormDialog({
  open,
  onOpenChange,
  orderId,
  orderNumber,
  items,
  requirement,
  onSaved,
}: SourcingFormDialogProps): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();

  const create = useCreateSourcingRequirement();
  const update = useUpdateSourcingRequirement();

  const [draft, setDraft] = useState<Draft>(() => draftOf(requirement));

  // Reseed whenever the dialog is opened on a different row, so editing one requirement and then
  // another does not show the first one's figures.
  useEffect(() => {
    if (open) setDraft(draftOf(requirement));
  }, [open, requirement]);

  const set = (patch: Partial<Draft>): void => setDraft((current) => ({ ...current, ...patch }));

  const quantity = parseQuantity(draft.quantity);
  const amountPaise = ((): number | null => {
    if (draft.amount.trim() === '') return 0;
    try {
      return parseRupees(draft.amount);
    } catch {
      return null;
    }
  })();

  const valid = quantity !== null && quantity > 0 && amountPaise !== null && amountPaise >= 0;
  const busy = create.isPending || update.isPending;

  const submit = async (): Promise<void> => {
    if (!valid) return;
    try {
      if (requirement) {
        await update.mutateAsync({
          sourcingId: requirement.id,
          patch: {
            type: draft.type,
            description: draft.description.trim(),
            quantity: quantity as number,
            unit: draft.unit,
            amountPaise: amountPaise as number,
            notes: draft.notes.trim(),
          },
        });
      } else {
        await create.mutateAsync({
          orderId,
          ...(draft.orderItemId ? { orderItemId: draft.orderItemId } : {}),
          type: draft.type,
          description: draft.description.trim(),
          quantity: quantity as number,
          unit: draft.unit,
          amountPaise: amountPaise as number,
          notes: draft.notes.trim(),
        });
      }
      toast({ title: t('sourcing.saved'), variant: 'success' });
      onSaved();
    } catch (error) {
      toast({
        title: error instanceof Error ? error.message : t('error.unexpected'),
        variant: 'destructive',
      });
    }
  };

  const selectClass = 'h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {requirement
              ? t('sourcing.form.editTitle', { number: orderNumber })
              : t('sourcing.form.createTitle', { number: orderNumber })}
          </DialogTitle>
          <DialogDescription>{t('sourcing.form.hint')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          {/* Fixed once the row exists, so it is offered on creation only. */}
          {!requirement ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="sourcing-item">{t('sourcing.form.garment')}</Label>
              <select
                id="sourcing-item"
                value={draft.orderItemId}
                onChange={(event) => set({ orderItemId: event.target.value })}
                className={selectClass}
              >
                <option value="">{t('sourcing.wholeOrder')}</option>
                {items.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.quantity} × {item.garmentTypeLabel}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="sourcing-type">{t('sourcing.form.type')}</Label>
            <select
              id="sourcing-type"
              value={draft.type}
              onChange={(event) => set({ type: event.target.value as SourcingType })}
              className={selectClass}
            >
              {SOURCING_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`sourcing.type.${type}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="sourcing-description">{t('sourcing.form.description')}</Label>
            <Input
              id="sourcing-description"
              value={draft.description}
              onChange={(event) => set({ description: event.target.value })}
              placeholder={t('sourcing.form.descriptionPlaceholder')}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="sourcing-quantity">{t('sourcing.form.quantity')}</Label>
              <Input
                id="sourcing-quantity"
                inputMode="decimal"
                value={draft.quantity}
                onChange={(event) => set({ quantity: event.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="sourcing-unit">{t('sourcing.form.unit')}</Label>
              <select
                id="sourcing-unit"
                value={draft.unit}
                onChange={(event) => set({ unit: event.target.value as StockUnit })}
                className={selectClass}
              >
                {STOCK_UNITS.map((unit) => (
                  <option key={unit} value={unit}>
                    {t(`sourcing.unit.${unit}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="sourcing-amount">{t('sourcing.form.amount')}</Label>
            <Input
              id="sourcing-amount"
              inputMode="decimal"
              value={draft.amount}
              onChange={(event) => set({ amount: event.target.value })}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="sourcing-notes">{t('sourcing.form.notes')}</Label>
            <Input
              id="sourcing-notes"
              value={draft.notes}
              onChange={(event) => set({ notes: event.target.value })}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button disabled={!valid || busy} onClick={() => void submit()}>
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SourcingFormDialog;
